<template>
  <WizardBox title="New Purchase Order">
      <template v-slot:tools>
        
      </template>
      <template v-slot:content>
        <FormGroup v-bind:form="orderForm">
          <FormWizard ref="orderWizard" :submitting="submitting" v-bind:tabList="tabList" @finished="handleSubmit" @stepChange="wizardStepChanged">
            <template v-slot:content>
              
              <FormWizardStep index="1" v-bind:active="true">
                <FormUploadDropTarget @filesDropped="$refs.documentUpload.filesDroppedFromTarget($event)">
                
                  <FormRow label="Type">
                    <FormOption label="Bulk" name="type" :setting="PurchaseOrderTypes.bulk" v-model="order.type" v-show="userHasCreatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrderBulk)"/>
                    <FormOption label="Standard" name="type" :setting="PurchaseOrderTypes.standard" v-model="order.type" v-show="userHasCreatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrderStandard)" />
                  </FormRow>
                  <FormRow label="Supplier">
                    <FormSelect :options="supplierLookup" v-model="order.contactId" field="contactId" />
                  </FormRow>
                  <FormRow label="Supplier Address">
                    <FormSearchableSelect
                      :options="contactAddressLookup"
                      v-model="order.contactAddressId"
                      field="contactAddressId"
                      :optionSlot="true">
                      <template v-slot:option="option">
                        <div class="p-1">
                          <LabelTags :labelList="option.data.deliveryLabelList" class="float-right" />
                          {{option.data.name}}
                        </div>                          
                      </template>
                    </FormSearchableSelect>
                  </FormRow>

                  <FormRow label="Receiving Location" helpTip="The location for this purchase order. All received goods will be assigned to this location">
                    <AccountLocationSelect v-model="order.accountLocationId" field="accountLocationId" />
                  </FormRow>

                  <FormRow label="Notes">
                    <FormInputTextArea v-model="order.notes" field="notes" placeholder="Supplier / Shipping Notes" large />
                  </FormRow>

                  <FormHorizontalRule />

                  <FormRow label="Document Uploads">
                    <FormUpload ref="documentUpload" v-model="order.uploadFileList" field="uploadFileList" />
                  </FormRow>
                </FormUploadDropTarget>

              </FormWizardStep>

              <FormWizardStep index="2">
                <span v-show="isSupplierSelected">
                  <div v-show="isTypeBulk">
                    <BulkOrderItemList
                      ref="bulkEntryList"
                      :editing="true"
                      v-model="order.bulkItemList"
                      :contactId="order.contactId"
                      :currencyCode="supplierData.supplierCurrencyCode"
                      @updateTotals="summaryBulkTotals = $event"
                      @updateSummary="summaryBulk = $event"
                    />
                  </div>
                  <div v-show="!isTypeBulk">
                    <OrderItemList 
                      ref="orderEntryList"
                      :editing="true"
                      v-model="order.processedItemList"
                      :contactId="order.contactId"
                      :showTotals="true"
                      :isDespatchDelivery="false"
                      :priceType="PriceTypes.supplier"
                      :optionDiscountAvailable="false"
                      :optionCustomerApprovalAvailable="false"
                      :optionCustomFieldsAvailable="false"
                      :optionExternalAvailable="false"
                      @updateTotals="summaryProcessedTotals = $event"
                 />
                  </div>
                </span>
                <p class="font-italic text-center" v-if="!isSupplierSelected">Supplier must be selected before continuing</p>
              </FormWizardStep>

              <FormWizardStep index="3">
                <p class="font-italic text-center" v-if="!isSupplierSelected">Supplier must be selected before continuing</p>
                <div v-show="isSupplierSelected">
                  <FormRow label="Despatch Method">
                    <FormOption label="Delivery" name="despatchType" :setting="DespatchTypes.delivery" v-model="order.despatchType" inline="true" />
                    <FormOption label="Pickup" name="despatchType" :setting="DespatchTypes.pickup" v-model="order.despatchType" inline="true" />
                  </FormRow>

                  <FormRow label="Date Required">
                    <FormDate v-model="order.dateRequired" field="dateRequired" />
                    <template v-if="supplierEarliestDeliveryDate">
                      <small class="text-muted">Supplier estimated earliest delivery date is: <strong>{{supplierEarliestDeliveryDate | formatDate}}</strong></small>
                    </template>
                  </FormRow>

                  <div v-show="isDespatchDelivery">
                    <FormHorizontalRule />
                    <FormRow label="Delivery Location">
                      <FormSelect :options="deliveryLocationLookup" v-model="order.deliveryAccountLocationId" field="deliveryAccountLocationId" />
                    </FormRow>

                    <FormRow label="Delivery Address 1">
                      <FormInputAddressSearch
                        placeholder="Address 1"
                        v-model="order.deliveryAddress1"
                        field="deliveryAddress1"
                        :readonly="!isAddressEditable"
                        @updateAddress2="order.deliveryAddress2 = $event"
                        @updateSuburb="order.deliverySuburb = $event"
                        @updatePostcode="order.deliveryPostcode = $event"
                        @updateState="order.deliveryState = $event"
                        @updateCountry="order.deliveryCountry = $event"
                      />
                    </FormRow>
                    <FormRow label="Delivery Address 2">
                      <FormInputText placeholder="Address 2" v-model="order.deliveryAddress2" field="deliveryAddress2" :readonly="!isAddressEditable" />
                    </FormRow>
                    <FormRow label="Delivery Suburb">
                      <FormInputText placeholder="Suburb" v-model="order.deliverySuburb" field="deliverySuburb" :readonly="!isAddressEditable" />
                    </FormRow>
                    <FormRow label="Delivery Postcode">
                      <FormInputText placeholder="Postcode" v-model="order.deliveryPostcode" field="deliveryPostcode" :readonly="!isAddressEditable" />
                    </FormRow>
                    <FormRow label="Delivery State">
                      <FormInputText placeholder="State" v-model="order.deliveryState" field="deliveryState" :readonly="!isAddressEditable" />
                    </FormRow>
                    <FormRow label="Delivery Country">
                      <FormInputText placeholder="Country" v-model="order.deliveryCountry" field="deliveryCountry" :readonly="!isAddressEditable" />
                    </FormRow>
                  </div>
                </div>
              </FormWizardStep>

              <FormWizardStep index="4">
                <p class="font-italic text-center" v-if="!isSupplierSelected">Supplier must be selected before continuing</p>
                <div class="row review-data" v-if="isSupplierSelected">
                  <div class="col-9">
                    <div class="row">
                      <div class="col-6">
                        <div class="review-header">
                          <h2>Purchase Order Summary</h2> <a class="review-edit-button" @click="displayDetailsTab"><i class="far fa-pencil"></i></a>
                        </div>
                        <FormRow label="Type" :labelColumnSize="5">
                          <LabelBadge :class="`label-po-type-${order.type}`">{{purchaseOrderTypeName}}</LabelBadge>
                        </FormRow>
                        <FormRow label="Location" :labelColumnSize="5">
                          <FormStaticLabel>{{formatLookupName(order.accountLocationId, accountLocationLookup)}}</FormStaticLabel>
                        </FormRow>
                        <FormRow label="Supplier" :labelColumnSize="5">
                          <LinkContact v-if="order.contactId" :id="supplierData.id" :name="supplierData.name" />
                        </FormRow>
                        <FormRow label="Container Code" :labelColumnSize="5" v-if="isTypeBulk">
                          <FormStaticLabel>{{summaryBulk.containerCode}}</FormStaticLabel>
                        </FormRow>
                        <FormRow label="Notes" :labelColumnSize="5">
                          <div style="white-space: pre;">{{order.notes | formatHyphenEmptyValue}}</div>
                        </FormRow>
                        <FormRow label="Despatch" :labelColumnSize="5">
                          <FormStaticLabel>
                            {{isDespatchDelivery ? 'Delivery' : ''}}
                            {{isDespatchPickup ? 'Pickup' : ''}}
                            <p v-if="!isDespatchDelivery && !isDespatchPickup" class="text-danger">None selected</p>
                            <div v-if="order.dateRequired">Date Required <span class="font-weight-bold">{{order.dateRequired | formatDate}}</span></div>
                          </FormStaticLabel>
                        </FormRow>
                      </div>

                      <div class="col-6" v-if="isDespatchDelivery">
                        <div class="review-header">
                          <h2>Delivered To</h2> <a class="review-edit-button" @click="displayDespatchTab"><i class="far fa-pencil"></i></a>
                        </div>
                        <div>{{formatLookupName(order.deliveryAccountLocationId, deliveryLocationLookup)}}</div>
                        <AddressDisplay
                          :address1="order.deliveryAddress1"
                          :address2="order.deliveryAddress2"
                          :suburb="order.deliverySuburb"
                          :state="order.deliveryState"
                          :postcode="order.deliveryPostcode"
                          :country="order.deliveryCountry"
                          />
                      </div>
                      <div class="col-6" v-if="isDespatchPickup">
                        <div class="review-header">
                          <h2>Pickup</h2> <a class="review-edit-button" @click="displayDespatchTab"><i class="far fa-pencil"></i></a>
                        </div>
                      </div>
                    </div>

                    <div class="review-header review-header-seperator">
                      <h2>Items</h2> <a class="review-edit-button" @click="displayItemsTab"><i class="far fa-pencil"></i></a>
                    </div>
                    <div v-if="isTypeBulk"> 
                      <BulkOrderItemList
                        :editing="false"
                        :showTotals="false"
                        :value="order.bulkItemList"
                        :contactId="order.contactId"
                        :currencyCode="supplierData.supplierCurrencyCode"
                      />

                      <p class="font-italic text-center text-muted" v-if="order.bulkItemList.length == 0">No items in the list</p>
                    </div>
                    <div v-if="isTypeStandard"> 
                      <OrderItemList
                        :editing="false"
                        :showTotals="false"
                        :value="order.processedItemList"
                        :contactId="order.contactId"
                        :isDespatchDelivery="false"
                        :priceType="PriceTypes.supplier"
                        :optionDiscountAvailable="false"
                        :optionCustomerApprovalAvailable="false"
                        :optionCustomFieldsAvailable="false"
                        :optionExternalAvailable="false"
                        />
                      <p class="font-italic text-center text-muted" v-if="order.processedItemList.length == 0">No items in the list</p>
                    </div>

                    <div>
                      <div class="row">
                        <div class="col-12" v-if="purchaseOrderHasNotices">
                          <div class="review-header review-header-seperator">
                            <h2>Notices</h2>
                          </div>
                          <p v-if="isRequiredDateBeforeLeadTime()" class="mb-1">
                            <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="order-item-icon-production-error" iconStack="fa-circle" iconStackClass="order-item-icon-production-error-bg" class="mr-1"></Icon>
                            Required date <strong>{{order.dateRequired | formatDate}}</strong> is <strong>before</strong> supplier's earliest estimated delivery date of <strong>{{supplierEarliestDeliveryDate | formatDate}}</strong>
                          </p>

                          <div v-if="isTypeBulk">
                            <p v-if="bulkPricingWarningCount > 0" class="mb-1">
                              <Icon iconStyle="fas" icon="fa-usd-circle" iconClass="order-item-icon-price-error" iconStack="fa-circle" iconStackClass="order-item-icon-price-error-bg" class="mr-1"></Icon>
                              Purchase Order has {{bulkPricingWarningCount}} item{{bulkPricingWarningCount | displayCountPlural}} with <strong>supplier price warnings</strong>
                            </p>
                            <p v-if="summaryBulk.containerOverCapacity" class="mb-1">
                              <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="order-item-icon-production-error" iconStack="fa-circle" iconStackClass="order-item-icon-production-error-bg" class="mr-1"></Icon>
                              Container is <strong>over capacity</strong>
                            </p>
                            <p v-if="summaryBulk.containsMixedSizes" class="mb-1">
                              <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="order-item-icon-production-error" iconStack="fa-circle" iconStackClass="order-item-icon-production-error-bg" class="mr-1"></Icon>
                              Container contains <strong>mixed sized glass</strong>
                            </p>
                            <p v-if="summaryBulk.containsMixedContainers" class="mb-1">
                              <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="order-item-icon-production-error" iconStack="fa-circle" iconStackClass="order-item-icon-production-error-bg" class="mr-1"></Icon>
                              Glass items link to <strong>mixed container types</strong>
                            </p>
                          </div>
                          <div v-if="isTypeStandard">
                            <p v-if="processedProductionWarningCount > 0" class="mb-1">
                              <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="order-item-icon-production-error" iconStack="fa-circle" iconStackClass="order-item-icon-production-error-bg" class="mr-1"></Icon>
                              Purchase Order has {{processedProductionWarningCount}} item{{processedProductionWarningCount | displayCountPlural}} with <strong>production</strong> errors
                            </p>
                            <p v-if="processedPricingWarningCount > 0" class="mb-1">
                              <Icon iconStyle="fas" icon="fa-usd-circle" iconClass="order-item-icon-price-error" iconStack="fa-circle" iconStackClass="order-item-icon-price-error-bg" class="mr-1"></Icon>
                              Purchase Order has {{processedPricingWarningCount}} item{{processedPricingWarningCount | displayCountPlural}} with <strong>pricing</strong> errors
                            </p>
                          </div>
                          Accept Warnings: <FormCheckbox v-model="order.acceptWarnings" />
                        </div>

                      </div>
                    </div>

                    <div class="row">
                      <div class="col-6" v-if="order.uploadFileList.length > 0">
                        <div class="review-header review-header-seperator">
                          <h2>Attached Files</h2> <a class="review-edit-button" @click="displayDetailsTab"><i class="far fa-pencil"></i></a>
                        </div>
                        <div v-for="file in order.uploadFileList" :key="file.key">
                          <FileAttachment :file="file" :isTempUpload="true" />
                        </div>
                        
                      </div>
                    </div>

                  </div>
                  <div class="col-3">
                    <div class="review-header">
                      <h2>Total</h2>
                    </div>
                    <div class="review-totals" v-if="isTypeBulk">
                      <div class="row"><div class="col-6">Sub Total</div><div class="col-6 text-right">{{summaryBulkTotals.subTotal | formatCurrency}}</div></div>
                      <div class="row"><div class="col-6">{{supplierTaxName}}</div><div class="col-6 text-right">{{summaryBulkTotals.tax | formatCurrency}}</div></div>
                      <div class="row font-weight-bold border-top">
                        <div class="col-6">Total</div>
                        <div class="col-6 text-right">{{summaryBulkTotals.total | formatCurrency}}</div>
                      </div>
                    </div>
                    <div class="review-totals" v-if="isTypeStandard">
                      <div class="row"><div class="col-6">Sub Total</div><div class="col-6 text-right">{{summaryProcessedTotals.subTotal | formatCurrency}}</div></div>
                      <div class="row"><div class="col-6">{{supplierTaxName}}</div><div class="col-6 text-right">{{summaryProcessedTotals.tax | formatCurrency}}</div></div>
                      <div class="row font-weight-bold border-top">
                        <div class="col-6">Total</div>
                        <div class="col-6 text-right">{{summaryProcessedTotals.total | formatCurrency}}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </FormWizardStep>

            </template>
          </FormWizard>
        </FormGroup>
      </template>
    </WizardBox>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import form from '@/mixins/form'
import orderItemMixin from '@/modules/orders/mixins/orderItemMixin'
import detectUnload from '@/mixins/detectUnload'

import WizardBox from '@/components/wizardBox'
import Utils from '@/utils/utils'

import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'

import BulkOrderItemList from '@/modules/purchasing/components/bulkOrderItemList'
import OrderItemList from '@/modules/orders/components/orderItemList'

import { PurchaseOrderTypes, PurchaseOrderTypeLookup, DespatchTypes, PriceTypes, InventoryStockTypes } from '@/store/types'

export default {
  name: 'PurchaseOrderNew',
  mixins: [
    form,
    detectUnload,
    orderItemMixin
  ],
  components: {
    WizardBox,
    BulkOrderItemList,
    OrderItemList,
    AccountLocationSelect
  },
  props: {
  },
  created() {
    this.PurchaseOrderTypes = PurchaseOrderTypes
    this.DespatchTypes = DespatchTypes
    this.PriceTypes = PriceTypes
  },
  beforeRouteLeave (to, from, next) {
    if(!this.$data.isPurchaseOrderEdited)
      return next(true)
    
    this.confirmMessage(`Cancel Purchase Order?`, `You have unsaved changes with the new Purchase Order, are you sure you want to leave?`)
    .then(success => {
      next(success)
    })
    .catch(function () {
      next(false)
    })
  },
  data() {
    return {
      orderForm: {},
      order: {
        type: PurchaseOrderTypes.bulk,
        despatchType: DespatchTypes.delivery,
        contactId: null,
        contactAddressId: null,
        contactPersonId: null,
        
        accountLocationId: null,           // Receiving account location
        deliveryAccountLocationId: null,   // Delivery location. Set to null for custom delivery addresses

        deliveryAddress1: '',
        deliveryAddress2: '',
        deliverySuburb: '',
        deliveryState: '',
        deliveryPostcode: '',
        deliveryCountry: '',

        bulkItemList: [],
        processedItemList: [],
        uploadFileList: [],
        acceptWarnings: false
      },
      supplierData: {},
      accountData: {},
      supplierLookup: [],
      deliveryLocationLookup: [],
      accountLocationLookup: [],

      summaryBulkTotals: {},
      summaryBulk: {},
      summaryProcessedTotals: {},

      isPurchaseOrderEdited: true,
      submitting: false,
      loading: false,
    }
  },
  watch: {
    'order.contactId': function(newVal, oldVal) {
      this.requestSupplierDetails()
    },
    'order.accountLocationId': function(newVal, oldVal) {
      this.order.deliveryAccountLocationId = this.order.accountLocationId
    },
    'order.deliveryAccountLocationId': function(newVal, oldVal) {
      this.updateDeliveryAddress(this.order.deliveryAccountLocationId)
    }
  },
  mounted() {
    // Need to load all the data before we can attempt any copy attempts (if requested)
    this.requestData()
    .then(() => {
      this.loading = false

      this.$nextTick(() => {
        // Default to PO type based on permissions
        if(!this.$route.params.copyFromPO) {
          if(!this.userHasCreatePermission(this.PermissionTypes.PurchasingPermissionTypes.purchaseOrderBulk))
            this.order.type = PurchaseOrderTypes.standard
        }

        // Load the entry list data
        Promise.all([
          this.$refs.bulkEntryList.requestData(),
          this.$refs.orderEntryList.requestData()
        ])
        .then(() => {

          // Check if we're copying the data from another purchase order
          if(this.$route.params.copyFromPO) {
            this.copySourcePurchaseOrder(this.$route.params.sourcePurchaseOrderId)
          }

          // Check if we're creating from a container purchase request
          if(this.$route.query.bulkData) {
            this.copyBulkDataFromQuery(this.$route.query.bulkData)
          }
        })
      })
    })
    .finally(() => {
      this.loading = false
    })
  },
  computed: {
    tabList() {
      var output = [
        { title: 'Details' },
        { title: 'Items' },
        { title: 'Despatch' },
        { title: 'Review' }
      ]
      return output
    },
    isTypeBulk() {
      return this.order.type == PurchaseOrderTypes.bulk
    },
    isTypeStandard() {
      return this.order.type == PurchaseOrderTypes.standard
    },
    isSupplierSelected() {
      return !!this.order.contactId
    },
    isDespatchDelivery() {
      return this.order.despatchType == DespatchTypes.delivery
    },
    isDespatchPickup() {
      return this.order.despatchType == DespatchTypes.pickup
    },
    isAddressEditable() {
      return !this.order.deliveryAccountLocationId    // Only if custom address selected
    },
    purchaseOrderTypeName() {
      return Utils.getLookupName(PurchaseOrderTypeLookup, this.order.type)
    },
    supplierEarliestDeliveryDate() {
      if(this.order.contactId == null || this.order.contactId == '')
        return null

      var leadDays = parseInt(this.supplierData.supplierDeliveryLeadDays)
      if(leadDays == 0 || isNaN(leadDays))
        return null

      return moment().add(leadDays, 'days').startOf('day').add(1, 'day')
    },
    supplierTaxName() {
      if(this.order.contactId == null || this.order.contactId == '')
        return 'Tax'
      if(!this.supplierData.supplierTaxRate)
        return 'Tax'
      return this.supplierData.supplierTaxRate.displayName
    },
    bulkPricingWarningCount() {
      return this.order.bulkItemList.filter(item => item.priceError != '').length
    },
    processedProductionWarningCount() {
      return this.order.processedItemList.filter(item => item.productionError != '').length
    },
    processedPricingWarningCount() {
      return this.order.processedItemList.filter(item => item.priceError != '').length
    },
    purchaseOrderHasNotices() {
      // Bulk
      if(this.isTypeBulk) {
        if(this.isRequiredDateBeforeLeadTime())
          return true
        if(this.bulkPricingWarningCount > 0)
          return true
        return (this.summaryBulk.containerOverCapacity || this.summaryBulk.containsMixedSizes || this.summaryBulk.containsMixedContainers)
      }
      // Processed
      if(this.isTypeStandard) {
        return this.processedProductionWarningCount > 0 || this.processedPricingWarningCount > 0
      }

      return false
    },
    contactAddressLookup() {
      var addressLookup = [{ name: 'None', value: null, labelList: [], deliveryLabelList: [] }]

      if(this.supplierData.addresses) {
        var addressLookup = _.concat(addressLookup, this.supplierData.addresses.map(address => {
          return {
            name: address.formattedAddress,
            value: address.id,
            labelList: address.labelList,
            deliveryLabelList: address.deliveryLabelList
          }
        }))
      }
      return addressLookup
    },
  },
  methods: {
    onBeforeUnload: function(event) {
      if(!this.$data.isPurchaseOrderEdited)
        return null

      var confirmationMessage = `You have unsaved changes with this new Purchase Order. Are you sure you want to exit?`

      event.returnValue = confirmationMessage
      return confirmationMessage
    },
    handleSubmit() {
      if(!this.$refs.documentUpload.validateUploadsComplete())
        return
      if(this.purchaseOrderHasNotices && !this.order.acceptWarnings) {
        this.errorMessage('Purchase Order has warnings', 'Please fix the warnings or accept the warnings before proceeding')
        return
      }

      this.submitting = true

      var orderData = _.cloneDeep(this.$data.order)
      this.$data.orderForm = this.clearFormResponse()

      if(this.isTypeStandard) {
        orderData.processedItemList = this.prepareOrderItemListForUpdate(this.order.processedItemList)
      }

      this.$apiService.purchasingService.createPurchaseOrder(orderData)
      .then(response => {
        this.submitting = false

        // Clear editing flag to prevent route guard confirmation
        this.$data.isPurchaseOrderEdited = false

        this.successToast('Purchase Order Created')
        this.$router.push(`/purchasing/purchaseorders/${response.data.id}`)
      })
      .catch(err => {
        // Process errors
        this.$data.orderForm = this.processFormResponse(err)
        this.$refs.orderWizard.displayErrors()

        this.submitting = false
      })
    },
    requestData() {
      return new Promise((resolve, reject) => {
        this.loading = true

        Promise.all([
          this.$apiService.contactService.getContactList({
            isActive: 1,
            isSupplier: 1
          }),
          this.$apiService.adminService.getAccount()
        ])
        .then(results => {
          let contactList = results[0].data
          let accountInfo = results[1].data
          let locationList = accountInfo.accountLocations.filter(location => location.isActive)

          this.supplierLookup = [{ value: null, name: 'None' }]
          this.supplierLookup = _.concat(this.supplierLookup, contactList.map(item => {
            return { value: item.id, name: item.name }
          }))

          this.accountData = accountInfo
          this.deliveryLocationLookup = [{ value: null, name: 'Custom' }]
          this.deliveryLocationLookup = _.concat(this.deliveryLocationLookup, locationList.map(location => {
            return { value: location.id, name: location.name }
          }))

          this.accountLocationLookup = locationList.map(location => {
            return { value: location.id, name: location.name }
          })

          // Select default location / delivery location
          if(locationList.length > 0) {
            this.order.accountLocationId = locationList[0].id
            this.order.deliveryAccountLocationId = this.order.accountLocationId
          }

          // Set default address
          this.updateDeliveryAddress(this.order.deliveryAccountLocationId)

          return resolve()
        })
        .finally(() => {
          this.loading = false
        })
      })
    },
    requestSupplierDetails() {
      if(this.order.contactId == null || this.order.contactId == '')
        return

      this.$apiService.contactService.getContact(this.order.contactId)
      .then(result => {
        this.supplierData = result.data

        // Select the default address
        // Next tick to allow address list to be updated first
        this.$nextTick(() => {
          let defaultAddress = this.supplierData.addresses.find(address => address.id == this.supplierData.defaultAddressId)
          this.order.contactAddressId = defaultAddress ? defaultAddress.id : null
        })
      })
    },

    copySourcePurchaseOrder(sourcePurchaseOrderId) {
      // Download an existing purchase order & copy in the data
      this.$apiService.purchasingService.getPurchaseOrder(sourcePurchaseOrderId)
      .then(async (response) => {
        // Copy over all applicable data
        this.order.type = response.data.type
        this.order.despatchType = response.data.despatchType
        this.order.contactId = response.data.contactId
        this.order.accountLocationId = response.data.accountLocationId
        this.order.deliveryAccountLocationId = response.data.deliveryAccountLocationId
        this.order.dateRequired = response.data.dateRequired
        this.order.notes = response.data.notes

        this.order.deliveryAddress1 = response.data.deliveryAddress1
        this.order.deliveryAddress2 = response.data.deliveryAddress2
        this.order.deliverySuburb = response.data.deliverySuburb
        this.order.deliveryState = response.data.deliveryState
        this.order.deliveryPostcode = response.data.deliveryPostcode
        this.order.deliveryCountry = response.data.deliveryCountry

        if(response.data.type == PurchaseOrderTypes.bulk) {
          for(var item of response.data.purchaseOrderItems) {

            await this.$refs.bulkEntryList.addBulkOrderItem(item)
          }
        }
        if(response.data.type == PurchaseOrderTypes.standard || response.data.type == PurchaseOrderTypes.externalOrder) {

          this.$refs.orderEntryList.copyOrderItemList(_.cloneDeep(response.data.purchaseOrderItems))
        }
      })
    },
    copyBulkDataFromQuery(bulkDataQuery) {
      try {
        // Copy bulk data from query string (encoded)
        let bulkData = JSON.parse(bulkDataQuery)

        this.order.type = PurchaseOrderTypes.bulk
        this.order.contactId = bulkData.contactId
        this.order.notes = bulkData.notes || ''
        this.order.dateRequired = bulkData.date || ''

        // Wait for supplier details to load
        this.$nextTick(async () => {
          for(let item of bulkData.items) {
            await this.$refs.bulkEntryList.addBulkOrderItem({
              stockType: InventoryStockTypes.glass,
              isCustomStockLinkedGlassSize: false,
              stockId: item.s,
              blockQuantity: item.q,
              glassSizeId: item.gsid
            })
          }
        })
      } catch(err) {
        console.error(err)
        this.errorMessage('Error', 'Error loading bulk data from request')
      }
    },

    // Check if the supplier's delivery lead time (plus current day) would be after the required date
    isRequiredDateBeforeLeadTime() {
      if(this.order.dateRequired == null || this.order.dateRequired == '')
        return false

      let earliestDate = this.supplierEarliestDeliveryDate
      if(earliestDate == null)
        return false

      var requiredDate = moment(this.order.dateRequired)
      if(earliestDate.isAfter(requiredDate))
        return true

      return false
    },
    wizardStepChanged(tabIndex) {
      // If review selected, regenerate the order item SVGs
      if(tabIndex == 3 && this.isTypeStandard) {
        this.regenerateOrderSVGs()
      }
    },

    // Regenerate the order item SVGs
    // Only done for non-complex order items
    regenerateOrderSVGs() {
      this.order.processedItemList.forEach((item, index) => {

        if(item.stockType == InventoryStockTypes.glass) {
          /*
          // TODO: Change to new method in order item diagram mixin
          
          this.$set(this.order.processedItemList, index, item)
          */
        }
      })
    },

    updateDeliveryAddress(accountLocationId) {
      // Fill in the delivery location details
      if(this.accountData.accountLocations) {
        var locationInfo = this.accountData.accountLocations.find(location => location.id == accountLocationId)
        if(locationInfo) {
          this.order.deliveryAddress1 = locationInfo.address1
          this.order.deliveryAddress2 = locationInfo.address2
          this.order.deliverySuburb = locationInfo.suburb
          this.order.deliveryState = locationInfo.state
          this.order.deliveryPostcode = locationInfo.postcode
          this.order.deliveryCountry = locationInfo.country
        }
      }
    },

    displayDetailsTab() {
      this.$refs.orderWizard.selectTab(0)
    },
    displayItemsTab() {
      this.$refs.orderWizard.selectTab(1)
    },
    displayDespatchTab() {
      this.$refs.orderWizard.selectTab(2)
    },
  }
};
</script>
